import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Icon, { GLYPHS } from '../../../../common/icon';
import { ELIGIBILITY_STEP_STATUS, END_OF_PAY_PERIOD_DAY_OPTIONS } from '../constants';
import AutoPayoutEligibilityStatus from './AutoPayoutEligibilityStatus';
import CheckboxSwitch from '../../../../common/checkboxSwitch';
import { formatPercentage } from '../../../../common/Percentage';
import Tooltip from '../../../../common/tooltip';
import Select from '../../../../common/select';
import { eligibilityStatus } from '../propTypes';
import EligibilityStep from './EligibilityStep';

const AutoPayout = ({
  isShopViewActive,
  howToEnableIsOn,
  howToEnableToggle,
  eligibilitySteps,
  overallEligibilityStatus,
  barbersWithIssues,
  isAutoPayoutEnabled,
  isAutoPayoutPaused,
  isAutoPayoutSettingsEditable,
  handleSubmit,
  dailyPayroll,
  dailyPayrollFee,
  commissionSplits,
  commissionSplitsFee,
  commissionSplitsShopPaysFees,
  isSaving,
  reset,
}) => (
  <div className="c-wrapper">
    <div className="u-max-width-710 u-margin-auto">
      <div
        display-if={isShopViewActive && isAutoPayoutPaused}
        className="c-autopayout__status c-autopayout__status-critical u-mb"
      >
        <div className="c-autopayout__status-icon c-autopayout__status-critical">i</div>
        <span>Auto Payouts paused, see below for steps to resume your Auto Payouts.</span>
      </div>
    </div>
    <div className="c-inner-wrapper u-max-width-710 u-br-12">
      <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">How it works</h2>
      <p className="c-heading--small c-autopayout__description u-mt-">
        Automate weekly payouts to commission barbers based on their commission split.
      </p>

      <Fragment display-if={isShopViewActive}>
        <div className="c-separator u-mt u-mb--" />

        <button
          type="button"
          onClick={howToEnableToggle}
          className="c-btn c-btn--transparent u-ph0 c-autopayout__section-header"
        >
          <div
            className={classnames(
              'c-settings-section__header-icon u-inline-block',
              { 'is-active': howToEnableIsOn }
            )}
          >
            <Icon glyph={GLYPHS.chevronDropdown} size="micro" />
          </div>
          <span className="u-ml-- u-type--uppercase">How to enable</span>
        </button>

        <div display-if={howToEnableIsOn} className="c-autopayout-steps">
          {eligibilitySteps.map(step => (
            <EligibilityStep
              key={step.id}
              stepId={step.id}
              status={step.status}
              barbersWithIssues={barbersWithIssues}
            />
          ))}
        </div>

        <AutoPayoutEligibilityStatus
          status={overallEligibilityStatus}
          isAutoPayoutPaused={isAutoPayoutPaused}
          isAutoPayoutEnabled={isAutoPayoutEnabled}
          display-if={
            overallEligibilityStatus !== ELIGIBILITY_STEP_STATUS.DONE
              || !isAutoPayoutEnabled
              || howToEnableIsOn
          }
        />
      </Fragment>

      <div className="c-autopayout__section-separator" />

      <form onSubmit={handleSubmit}>
        <fieldset
          className={classnames(
            'c-autopayout__section',
            { 'c-autopayout__section-disabled': !isAutoPayoutSettingsEditable && isShopViewActive }
          )}
          disabled={!isAutoPayoutSettingsEditable || !isShopViewActive}
        >
          <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">Auto Commission Splits</h2>
          <p className="c-heading--small c-autopayout__description u-mt-">
            Enable this to set up automatic commission split deposits for both shops and barbers on a weekly basis,
            and send all tips directly to barbers&apos; accounts on a daily basis.
          </p>
          <div className="o-flexbox o-flexbox--content-align-end u-mt">
            <CheckboxSwitch
              name="commissionSplits"
              id="commissionSplits"
              label={commissionSplits ? 'Enabled' : 'Disabled'}
            />
            <div>
              <span className={classnames('c-label-new', { 'c-autopayout__text-black': commissionSplits })}>
                Auto Commission Split Fee: {' '}
                {formatPercentage(commissionSplitsFee, 2)}
              </span>
              <Tooltip
                className="c-info-tooltip-new c-info-tooltip-new--form"
                text="This fee is assessed on each weekly Auto Commission Split."
              />
            </div>
          </div>

          <div className="c-separator u-mv" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">Shop-covered Fees</h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            When disabled, the shop and barber split fees based off the barber&apos;s commission split.
          </p>
          <CheckboxSwitch
            name="commissionSplitsShopPaysFees"
            id="commissionSplitsShopPaysFees"
            label={commissionSplitsShopPaysFees ? 'Enabled' : 'Disabled'}
            disabled={!commissionSplits}
          />

          <div className="c-separator u-mv" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">Payment date & time</h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            Determine what day your barber&apos;s weekly commission split is sent.
          </p>
          <div className="o-flexbox layout">
            <div className="layout__item u-1/2-medium-and-up">
              <div className="c-label-new">End of Pay Period Day</div>
              <Select
                className="c-select"
                name="commissionPayoutDayOfWeek"
                parse={val => parseInt(val, 10)}
                options={END_OF_PAY_PERIOD_DAY_OPTIONS}
                empty="Please select"
                disabled={!commissionSplits}
              />
            </div>
            <div className="layout__item u-1/2-medium-and-up">
              <div className="c-label-new">Length of Pay Period</div>
              <input
                className="c-input c-autopayout__disabled-input"
                value="One week"
                disabled
              />
            </div>
          </div>

          <div className="c-separator u-mb" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">Cash Goes to Barbers</h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            Enable this feature to grant barbers the ability to receive instant cash payments directly,
            bypassing the shop&apos;s deposit process.
          </p>
          <div className="u-flex">
            <CheckboxSwitch
              name="commissionSplitsCashToBarbersServices"
              id="commissionSplitsCashToBarbersServices"
              label="For Services"
              disabled={!commissionSplits}
            />
            <Tooltip
              className="c-info-tooltip-new c-info-tooltip-new--form u-ml0 u-mr u-mt--"
              text="Enabling this indicates that your barbers are expected pocket cash payments for service sales,
              and therefore don't need to be Auto Paid for those services."
            />

            <CheckboxSwitch
              name="commissionSplitsCashToBarbersProducts"
              id="commissionSplitsCashToBarbersProducts"
              label="For Products"
              disabled={!commissionSplits}
            />
            <Tooltip
              className="c-info-tooltip-new c-info-tooltip-new--form u-ml0 u-mt--"
              text="Enabling this indicates that your barbers are expected pocket cash payments for product sales,
              and therefore don't need to be Auto Paid for those products."
            />
          </div>
        </fieldset>

        <div className="c-autopayout__section-separator" />

        <fieldset
          className={classnames(
            'c-autopayout__section',
            { 'c-autopayout__section-disabled': !isAutoPayoutSettingsEditable && isShopViewActive }
          )}
          disabled={!isAutoPayoutSettingsEditable || !isShopViewActive}
        >
          <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">
            Daily Shop Payouts
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt-">
            Enable this feature to automate daily payouts of sales to the shop,
            without considering commission splits,
            and send all tips directly to barbers&apos; accounts on a daily basis.
          </p>
          <div className="o-flexbox o-flexbox--content-align-end u-mt">
            <CheckboxSwitch
              name="dailyPayroll"
              id="dailyPayroll"
              label={dailyPayroll ? 'Enabled' : 'Disabled'}
            />
            <div>
              <span className={classnames('c-label-new', { 'c-autopayout__text-black': dailyPayroll })}>
                Daily Shop Payout Fee: {formatPercentage(dailyPayrollFee, 2)}
              </span>
              <Tooltip
                className="c-info-tooltip-new c-info-tooltip-new--form"
                text="This fee is assessed on tips sent to barber accounts."
              />
            </div>
          </div>
        </fieldset>

        <Fragment display-if={isShopViewActive}>
          <div className="c-autopayout__section-separator" />

          <div className="u-flex o-flexbox--justify-end">
            <button
              type="button"
              className="c-btn c-btn--grey u-width-120 u-mr--"
              onClick={reset}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="c-btn u-width-120"
              disabled={!isAutoPayoutSettingsEditable || isSaving}
            >
              Save
            </button>
          </div>
        </Fragment>
      </form>
    </div>
  </div>
);

AutoPayout.propTypes = {
  isShopViewActive: PropTypes.bool.isRequired,
  howToEnableIsOn: PropTypes.bool.isRequired,
  howToEnableToggle: PropTypes.func.isRequired,
  eligibilitySteps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: eligibilityStatus.isRequired,
    })
  ).isRequired,
  overallEligibilityStatus: eligibilityStatus.isRequired,
  barbersWithIssues: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAutoPayoutPaused: PropTypes.bool.isRequired,
  isAutoPayoutEnabled: PropTypes.bool.isRequired,
  isAutoPayoutSettingsEditable: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dailyPayroll: PropTypes.bool.isRequired,
  dailyPayrollFee: PropTypes.number.isRequired,
  commissionSplits: PropTypes.bool.isRequired,
  commissionSplitsFee: PropTypes.number.isRequired,
  commissionSplitsShopPaysFees: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
};

export default AutoPayout;
